import { useState } from "react";
import { useTranslation } from "react-i18next";
import { renameConnector, type ListedConnector } from "../../../api/connectorsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";

/** The longest name the server takes, as long as a computer's own name may be. */
const MAXIMUM_NAME_LENGTH = 80;

/**
 * Renaming a PC (F78, W-06): the name its owner gives it, shown everywhere; clearing it goes
 * back to the computer's own name. The server keeps it, so every view and PC agrees.
 */
export default function PcRename({ connector, onRenamed }: { connector: ListedConnector; onRenamed: () => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(connector.name);
  const { state, submit } = useSubmission();
  const saving = state.status === "submitting";
  const save = (given: string | null) => {
    submit(async () => {
      await renameConnector(connector.connectorId, given);
      setEditing(false);
      onRenamed();
    });
  };
  if (!editing) {
    return (
      <AppButton type="button" variant="secondary" size="xs" onClick={() => { setName(connector.name); setEditing(true); }}>
        {t("connectors.list.rename")}
      </AppButton>
    );
  }
  return (
    <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); save(name.trim() === "" ? null : name.trim()); }}>
      <Field label={t("connectors.list.pcName")}>
        {(control) => <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={name} maxLength={MAXIMUM_NAME_LENGTH} onChange={(event) => { setName(event.target.value); }} />}
      </Field>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact /> : null}
      <div className="flex flex-wrap gap-2">
        <AppButton type="submit" variant="primary" size="xs" disabled={saving}>{t("connectors.list.saveName")}</AppButton>
        <AppButton type="button" variant="secondary" size="xs" disabled={saving} onClick={() => { setEditing(false); }}>{t("common.cancel")}</AppButton>
        {connector.givenName === null ? null : (
          <AppButton type="button" variant="ghost" size="xs" disabled={saving} onClick={() => { save(null); }}>{t("connectors.list.useComputerName", { name: connector.device.label })}</AppButton>
        )}
      </div>
    </form>
  );
}
